import type { MessageDto } from "@multi-chat/shared";

const MS_PER_DAY = 86_400_000;
const WEEKDAY_LOOKBACK_DAYS = 6;

type Translate = (key: string) => string;

/** Same timestamp source as bubble time: COALESCE(platformSentAt, createdAt). */
export function messageDisplayTimestampIso(message: MessageDto): string {
  return message.platformSentAt ?? message.createdAt;
}

/**
 * Local calendar day key (year-month-day in the runtime timezone).
 * Matches `formatMessageTime`, which formats with Intl and no explicit timeZone.
 */
export function localCalendarDayKey(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function shouldShowConversationDateSeparator(
  previous: MessageDto | undefined,
  current: MessageDto,
): boolean {
  const currentKey = localCalendarDayKey(messageDisplayTimestampIso(current));
  if (currentKey == null) return false;
  if (previous == null) return true;
  const previousKey = localCalendarDayKey(messageDisplayTimestampIso(previous));
  return previousKey !== currentKey;
}

export function conversationDateLabel(
  iso: string,
  locale: string,
  nowMs: number,
  t: Translate,
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date(nowMs);
  if (Number.isNaN(now.getTime())) return formatCalendarDate(date, locale);

  const diffDays = localDayDiff(now, date);
  if (diffDays === 0) return t("inbox.date.today");
  if (diffDays === 1) return t("inbox.date.yesterday");
  if (diffDays >= 2 && diffDays <= WEEKDAY_LOOKBACK_DAYS) {
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
  }
  return formatCalendarDate(date, locale);
}

/** Whole local calendar days from `then` to `now` (positive when `then` is in the past). */
export function localDayDiff(now: Date, then: Date): number {
  const nowStart = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const thenStart = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  return Math.round((nowStart - thenStart) / MS_PER_DAY);
}

function formatCalendarDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}
